export const MISSING_CLERK_SESSION_RELOAD_KEY = 'reloadedDueToMissingClerkSession';
export const LATE_CLERK_SESSION_RELOAD_KEY = 'reloadedDueToLateClerkSession';

/** Same as `getCookieSuffix` in `@clerk/shared/keys`, which the package doesn't export. */
export const getClerkCookieSuffix = async (
  publishableKey: string,
  subtle: SubtleCrypto = globalThis.crypto.subtle
): Promise<string> => {
  const digest = await subtle.digest('SHA-1', new TextEncoder().encode(publishableKey));
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .substring(0, 8);
};

/**
 * Clerk keeps `__client_uat` on the root domain at `0` while nobody is signed in. Dev and production both
 * write the unsuffixed name on `.picflow.com`, so the suffixed one (per Clerk instance) wins, as in clerk-js.
 */
export const clerkCookiesShowSignedOut = (cookies: Record<string, string>, cookieSuffix: string): boolean => {
  const clientUat = cookies[`__client_uat_${cookieSuffix}`] || cookies['__client_uat'] || '0';
  return Number(clientUat) === 0;
};

export const isAuthPath = (pathname: string): boolean => {
  return pathname === '/auth' || pathname.startsWith('/auth/');
};

export const isAppPath = (pathname: string): boolean => {
  return pathname === '/a' || pathname.startsWith('/a/');
};

/** Routes that mount Clerk sign-in/sign-up UI. */
export const needsClerkUiPath = (pathname: string): boolean => {
  return /\/auth\/(login|signup)/.test(pathname);
};

export const isPicflowTenantHost = (hostname: string): boolean => {
  const host = hostname.replace(/^www\./, '');
  if (!host.endsWith('.picflow.com')) return false;
  const subDomain = host.slice(0, -'.picflow.com'.length);
  return !['', 'dev', 'test', 'pwa'].includes(subDomain);
};

export const isPublicGalleryPage = (pathname: string, hostname: string, isTenantDomain: boolean): boolean => {
  if (isAuthPath(pathname) || isAppPath(pathname)) return false;
  return isTenantDomain || isPicflowTenantHost(hostname);
};

export const shouldShowClerkLoadToast = (pathname: string): boolean => {
  return isAuthPath(pathname) || isAppPath(pathname);
};

export const getClerkErrorCode = (err: unknown): string | undefined => {
  if (!err || typeof err !== 'object' || !('code' in err)) return;
  const code = (err as { code: unknown }).code;
  return typeof code === 'string' ? code : undefined;
};

export const getClerkErrorMessage = (err: unknown): string => {
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return '';
};

export const isClerkUiLoadError = (err: unknown): boolean => {
  const code = getClerkErrorCode(err);
  if (code === 'failed_to_load_clerk_ui') return true;
  return getClerkErrorMessage(err).includes('Failed to load Clerk UI');
};

export const isLocalStorageClerkError = (err: unknown): boolean => {
  const message = getClerkErrorMessage(err);
  return (
    message === 'The operation is insecure.' ||
    message ===
      `SecurityError: Failed to read the 'localStorage' property from 'Window': Access is denied for this document.`
  );
};

export const isDynamicImportClerkError = (err: unknown): boolean => {
  const message = getClerkErrorMessage(err);
  return (
    message === 'Importing a module script failed.' || message.includes('Failed to fetch dynamically imported module')
  );
};

export const isClerkLoadTimeout = (err: unknown): boolean => {
  return getClerkErrorMessage(err) === 'CLERK_LOAD_TIMEOUT';
};

export const clerkFailureSentryLevel = (opts: {
  isUiError: boolean;
  inAppBrowser: boolean;
  isPublicGallery: boolean;
}): 'warning' | 'error' => {
  if (opts.isUiError && (opts.inAppBrowser || opts.isPublicGallery)) return 'warning';
  return 'error';
};

export const toReportableError = (err: unknown, fallbackMessage: string): Error => {
  if (err instanceof Error) return err;
  return new Error(getClerkErrorMessage(err) || fallbackMessage);
};
